import {createRouter, createWebHashHistory, createWebHistory} from 'vue-router';
import UserView from '../api/models/view/UserView';
import {getCurrentLocale} from '../stores/localeStore';
import {authenticationCallbackRoute} from './routeActions/authenticationCallback';
import {confirmUser} from './routeActions/confirmUser';
import {PaymentCanceled} from './routeActions/paymentCanceled';
import {logoutMicrosoft} from './routeActions/logoutMicrosoft';
import {requireAuth} from './guards/requireAuth';
import {requireLicense} from './guards/requireLicense';
import {useAccountStore} from '../stores/accountStore';
import {useUserStore} from '../stores/userStore';
import {checkForTargetRouteAfterLogin} from './guards/checkForTargetRouteAfterLogin';
import {wrapRouteGuards} from './guardHelper';
import {resendSSOVerification} from './routeActions/resendSSOVerification';
import {handleQrCode} from './guards/qrCodeGuard';
import {handleLegacyQrCode} from './guards/legacyQrCodeGuard';
import {useFeatureFlagStore} from '../stores/featureFlagStore';
import {beforeEnterBooking, beforeEnterDetailPage} from './guards/bookingRouteGuard';
import {handleCheckIn} from './guards/checkInGuard';
import {handleAuthorizeSensor} from './guards/authorizeSensorGuard';
import {beforeEnterFavoriteSeatsPage} from './guards/sunsetRouteGuard';
import {beforeEnterVisitorManagement} from './guards/visitorManagementGuard';
import {useImpersonatingStore} from '../stores/impersonatingStore';
import {handleFeatureFlagGuard, requireOauthFeature} from './guards/featureFlagGuard';
import {storeToRefs} from 'pinia';

const routes = [
    {
        path: '/',
        beforeEnter: [requireAuth, checkForTargetRouteAfterLogin],
        component: () => import('../layout/AuthenticatedLayout.vue'),
        redirect: () => ({name: 'Home'}),
        children: [
            {
                path: '',
                name: 'FrontStage',
                redirect: () => ({name: 'Home'}),
                component: () => import('../layout/frontstage/FrontstageLayout.vue'),
                children: [
                    {
                        path: '',
                        name: 'Home',
                        component: () => import('../frontstage/components/home/HomeView.vue'),
                    },
                    {
                        path: 'archive',
                        name: 'Archive',
                        component: () => import('../frontstage/components/archive/ArchiveView.vue'),
                        beforeEnter: () => {
                            return useFeatureFlagStore().receiveNotifications.isReady();
                        },
                    },
                    {
                        path: '',
                        name: 'Licensed',
                        beforeEnter: [requireLicense],
                        children: [
                            {
                                path: 'settings',
                                children: [
                                    {
                                        path: '',
                                        name: 'UserSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/UserSettings.vue'),
                                    },
                                    {
                                        path: 'booking',
                                        name: 'BookingSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/BookingSettings.vue'),
                                        beforeEnter: async (_, from) => {
                                            const {bookingForOthers} =
                                                storeToRefs(useFeatureFlagStore());

                                            return await handleFeatureFlagGuard(
                                                bookingForOthers,
                                                from
                                            );
                                        },
                                    },
                                    {
                                        path: 'booking/add',
                                        name: 'BookingSettingsAdd',
                                        component: () =>
                                            import('../frontstage/components/settings/BookingSettingsAdd.vue'),
                                        beforeEnter: async (_, from) => {
                                            const {bookingForOthers} =
                                                storeToRefs(useFeatureFlagStore());

                                            return await handleFeatureFlagGuard(
                                                bookingForOthers,
                                                from
                                            );
                                        },
                                    },
                                    {
                                        path: 'appearance',
                                        name: 'AppearanceSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/AppearanceSettings.vue'),
                                    },
                                    {
                                        path: 'room',
                                        name: 'RoomSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/RoomSettings.vue'),
                                    },
                                    {
                                        path: 'week',
                                        name: 'WeekSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/WeekSettings.vue'),
                                    },
                                    {
                                        path: 'timezone',
                                        name: 'TimezoneSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/TimezoneSettings.vue'),
                                    },
                                    {
                                        path: 'password',
                                        name: 'PasswordSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/PasswordSettings.vue'),
                                    },
                                    {
                                        path: 'profileImage',
                                        name: 'ProfileImageSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/ProfileImageSettings.vue'),
                                    },
                                    {
                                        path: 'licensePlate',
                                        name: 'LicensePlateSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/LicensePlateSettings.vue'),
                                        beforeEnter: async (_, from) => {
                                            const {licensePlates} =
                                                storeToRefs(useFeatureFlagStore());

                                            return await handleFeatureFlagGuard(
                                                licensePlates,
                                                from
                                            );
                                        },
                                    },
                                    {
                                        path: 'calendar',
                                        name: 'DelegatedCalendarSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/DelegatedCalendarSettings.vue'),
                                        beforeEnter: async (_, from) => {
                                            const {delegatedCalendarAccess} =
                                                storeToRefs(useFeatureFlagStore());

                                            return await handleFeatureFlagGuard(
                                                delegatedCalendarAccess,
                                                from
                                            );
                                        },
                                    },
                                    {
                                        path: 'booking-summary',
                                        name: 'BookingSummarySettings',
                                        component: () =>
                                            import('../frontstage/components/settings/BookingSummarySettings.vue'),
                                        beforeEnter: () => {
                                            const {isImpersonating} = useImpersonatingStore();
                                            if (isImpersonating) {
                                                return {name: 'UserSettings'};
                                            }
                                            return useFeatureFlagStore().userBookingSummary.isReady();
                                        },
                                    },
                                    {
                                        path: 'notifications',
                                        name: 'NotificationSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/NotificationSettings.vue'),
                                        beforeEnter: async (_, from) => {
                                            const {notificationPreferences} =
                                                storeToRefs(useFeatureFlagStore());

                                            return await handleFeatureFlagGuard(
                                                notificationPreferences,
                                                from
                                            );
                                        },
                                    },
                                    {
                                        path: 'connected-apps',
                                        name: 'ConnectedAppsSettings',
                                        component: () =>
                                            import('../frontstage/components/settings/ConnectedAppsSettings.vue'),
                                        beforeEnter: requireOauthFeature,
                                    },
                                ],
                            },
                            {
                                path: 'overview/:selectedDay?/:selectedResourceId?/:selectedUserMeBookForId?',
                                name: 'DayBookingOverview',
                                component: () =>
                                    import('../frontstage/components/overview/DayBookingOverview.vue'),
                            },
                            {
                                path: 'weekOverview',
                                name: 'WeekOverview',
                                component: () =>
                                    import('../frontstage/components/weekOverview/WeekOverview.vue'),
                                beforeEnter: () => {
                                    //TODO: add global handling after failure
                                    const {account} = useAccountStore();
                                    return !account.anonymous;
                                },
                            },
                            {
                                path: '/qr/:resourceId',
                                name: 'QrCodeHandling',
                                beforeEnter: [handleQrCode],
                            },
                            {
                                //do not remove (qr code backwards compatibility)
                                path: 'confirmation/qr/:seatId?',
                                name: 'DeprecatedQrCodeDayBookingConfirmation',
                                beforeEnter: [handleLegacyQrCode],
                            },
                            {
                                //do not remove (qr code backwards compatibility)
                                path: 'confirmation/:seatId?',
                                name: 'DayBookingConfirmationLegacy',
                                beforeEnter: [handleLegacyQrCode],
                            },
                            {
                                path: 'favorites',
                                name: 'FavoriteSeats',
                                component: () =>
                                    import('../frontstage/components/FavoriteSeats.vue'),
                                beforeEnter: beforeEnterFavoriteSeatsPage,
                            },
                            {
                                path: 'present/:selectedDay?',
                                name: 'PresentList',
                                component: () => import('../frontstage/components/PresentList.vue'),
                                beforeEnter: () => {
                                    //TODO: add global handling after failure
                                    const {account} = useAccountStore();
                                    return !account.anonymous;
                                },
                            },
                            {
                                path: 'visitor-management',
                                name: 'VisitorManagement',
                                component: () =>
                                    import('../frontstage/components/visitorManagement/VisitorManagementList.vue'),
                                beforeEnter: beforeEnterVisitorManagement,
                            },
                            {
                                path: 'resource/:resourceId',
                                name: 'BookingResourceDetails',
                                components: {
                                    default: () =>
                                        import('../frontstage/components/booking/details/DetailsView.vue'),
                                    headerTopRight: () =>
                                        import('../frontstage/components/booking/details/DetailsTopRightView.vue'),
                                },
                                beforeEnter: beforeEnterDetailPage,
                                props: true,
                            },
                            {
                                path: 'booking',
                                name: 'Booking',
                                components: {
                                    default: () =>
                                        import('../frontstage/components/booking/BookingView.vue'),
                                    headerTopRight: () =>
                                        import('../frontstage/components/booking/header/BookingHeaderTopRight.vue'),
                                    headerExtension: () =>
                                        import('../frontstage/components/booking/header/BookingHeaderExtension.vue'),
                                },
                                beforeEnter: beforeEnterBooking,
                                redirect: () => ({name: 'BookingLocationSelection'}),
                                children: [
                                    {
                                        path: 'overview',
                                        name: 'BookingLocationSelection',
                                        component: () =>
                                            import('../frontstage/components/booking/selection/LocationSelection.vue'),
                                    },
                                    {
                                        path: 'location/:locationId',
                                        name: 'BookingFloorSelection',
                                        component: () =>
                                            import('../frontstage/components/booking/selection/FloorSelection.vue'),
                                        props: true,
                                    },
                                    {
                                        path: 'floor/:floorId',
                                        name: 'BookingRoomSelection',
                                        component: () =>
                                            import('../frontstage/components/booking/selection/RoomSelection.vue'),
                                        props: true,
                                    },
                                    {
                                        path: 'room/:roomId',
                                        name: 'BookingMap',
                                        component: () =>
                                            import('../frontstage/components/booking/map/BookingMap.vue'),
                                        props: true,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            ...(__IS_HYBRID_APP__
                ? []
                : [
                      {
                          path: '/admin/deskly-connect/sensor-connection',
                          name: 'SensorConnection',
                          component: () =>
                              import('../admin/components/desklyConnect/SensorConnection.vue'),
                          beforeEnter: async to => {
                              const {desklyConnect} = storeToRefs(useFeatureFlagStore());

                              // Check for sensorId
                              if (!to.query.sensorId) {
                                  return {name: 'Home'};
                              }

                              // Check feature flag
                              const result = await handleFeatureFlagGuard(desklyConnect);
                              if (result === false) {
                                  return {name: 'Home'};
                              }
                          },
                          meta: {role: UserView.ROLE_ADMIN},
                      },
                      {
                          path: '/admin',
                          name: 'Admin',
                          component: () => import('../layout/admin/AdminLayout.vue'),
                          meta: {role: UserView.ROLE_ANALYST, hideNavbar: true},
                          redirect: () => ({name: 'AdminDashboard'}),
                          beforeEnter: [requireLicense],
                          children: [
                              {
                                  path: 'dashboard',
                                  name: 'AdminDashboard',
                                  component: () => import('../admin/components/Dashboard.vue'),
                                  meta: {role: UserView.ROLE_ANALYST},
                              },
                              {
                                  path: 'analytics',
                                  meta: {role: UserView.ROLE_ANALYST},
                                  children: [
                                      {
                                          path: 'user-status',
                                          name: 'UserStatusAnalytics',
                                          component: () =>
                                              import('../admin/components/UserStatusAnalyticsView.vue'),
                                      },
                                      {
                                          path: 'resources',
                                          name: 'OccupancyAnalytics',
                                          component: () =>
                                              import('../admin/components/OccupancyAnalyticsView.vue'),
                                      },
                                      {
                                          path: 'occupancy',
                                          name: 'OccupancyView',
                                          component: () =>
                                              import('../admin/components/occupancy/OccupancyView.vue'),
                                          redirect: () => ({name: 'OccupancyLocationView'}),
                                          beforeEnter: () => {
                                              const {occupancyAccess} =
                                                  storeToRefs(useFeatureFlagStore());
                                              return handleFeatureFlagGuard(occupancyAccess);
                                          },
                                          children: [
                                              {
                                                  path: 'location',
                                                  name: 'OccupancyLocationView',
                                                  component: () =>
                                                      import('../admin/components/occupancy/views/OccupancyLocationView.vue'),
                                              },
                                              {
                                                  path: 'floor/:locationId',
                                                  name: 'OccupancyFloorView',
                                                  component: () =>
                                                      import('../admin/components/occupancy/views/OccupancyFloorView.vue'),
                                                  props: true,
                                              },
                                              {
                                                  path: 'room/:floorId',
                                                  name: 'OccupancyRoomView',
                                                  component: () =>
                                                      import('../admin/components/occupancy/views/OccupancyRoomView.vue'),
                                                  props: true,
                                              },
                                              {
                                                  path: 'map/:roomId',
                                                  name: 'OccupancyMap',
                                                  component: () =>
                                                      import('../admin/components/occupancy/map/OccupancyMap.vue'),
                                                  props: true,
                                              },
                                          ],
                                      },
                                  ],
                              },
                              {
                                  path: 'dayBookings/:accountId?',
                                  name: 'AdminDayBookingList',
                                  component: () => import('../admin/components/DayBookingList.vue'),
                                  meta: {role: UserView.ROLE_ANALYST},
                              },
                              {
                                  path: 'account',
                                  name: 'AdminAccountList',
                                  component: () => import('../admin/components/AccountList.vue'),
                                  meta: {role: UserView.ROLE_SUPERADMIN},
                              },
                              {
                                  path: 'account/add',
                                  name: 'AdminAccountAdd',
                                  component: () =>
                                      import('../admin/components/forms/AccountAdd.vue'),
                                  meta: {role: UserView.ROLE_SUPERADMIN},
                              },
                              {
                                  path: 'account/:accountId',
                                  name: 'AdminAccountEdit',
                                  component: () =>
                                      import('../admin/components/forms/AccountEdit.vue'),
                                  meta: {role: UserView.ROLE_ADMIN},
                              },
                              {
                                  path: 'feature-flags',
                                  name: 'AdminFeatureFlagManagement',
                                  component: () =>
                                      import('../admin/components/featureFlagManagement/FeatureFlagManagement.vue'),
                                  meta: {role: UserView.ROLE_SUPERADMIN},
                              },
                              {
                                  path: 'custom-event-definitions',
                                  name: 'AdminCustomEventDefinitionList',
                                  component: () =>
                                      import('../admin/components/customEvents/CustomEventDefinitionsList.vue'),
                                  meta: {role: UserView.ROLE_SUPERADMIN},
                                  beforeEnter: async (_, from) => {
                                      const {hubspotCustomEventDefinitions} =
                                          storeToRefs(useFeatureFlagStore());
                                      return await handleFeatureFlagGuard(
                                          hubspotCustomEventDefinitions,
                                          from
                                      );
                                  },
                              },
                              {
                                  path: 'custom-event-definitions/add',
                                  name: 'AdminCustomEventDefinitionAdd',
                                  component: () =>
                                      import('../admin/components/customEvents/CustomEventDefinitionAdd.vue'),
                                  meta: {role: UserView.ROLE_SUPERADMIN},
                                  beforeEnter: async (_, from) => {
                                      const {hubspotCustomEventDefinitions} =
                                          storeToRefs(useFeatureFlagStore());
                                      return await handleFeatureFlagGuard(
                                          hubspotCustomEventDefinitions,
                                          from
                                      );
                                  },
                              },
                              {
                                  path: 'custom-event-definitions/:id',
                                  name: 'AdminCustomEventDefinitionDetail',
                                  component: () =>
                                      import('../admin/components/customEvents/CustomEventDefinitionDetail.vue'),
                                  meta: {role: UserView.ROLE_SUPERADMIN},
                                  beforeEnter: async (_, from) => {
                                      const {hubspotCustomEventDefinitions} =
                                          storeToRefs(useFeatureFlagStore());
                                      return await handleFeatureFlagGuard(
                                          hubspotCustomEventDefinitions,
                                          from
                                      );
                                  },
                              },
                              {
                                  path: 'custom-event-definitions/:id/edit',
                                  name: 'AdminCustomEventDefinitionEdit',
                                  component: () =>
                                      import('../admin/components/customEvents/CustomEventDefinitionEdit.vue'),
                                  meta: {role: UserView.ROLE_SUPERADMIN},
                                  beforeEnter: async (_, from) => {
                                      const {hubspotCustomEventDefinitions} =
                                          storeToRefs(useFeatureFlagStore());
                                      return await handleFeatureFlagGuard(
                                          hubspotCustomEventDefinitions,
                                          from
                                      );
                                  },
                              },
                              {
                                  path: 'authentication',
                                  name: 'AdminAuthenticationEdit',
                                  component: () =>
                                      import('../admin/components/forms/AuthenticationEdit.vue'),
                                  meta: {role: UserView.ROLE_ADMIN},
                              },
                              {
                                  path: 'calendar-settings',
                                  name: 'AdminCalendarSettings',
                                  component: () =>
                                      import('../admin/components/CalendarSettings.vue'),
                                  beforeEnter: async (_, from) => {
                                      const {outlookCalendarSync, delegatedCalendarAccess} =
                                          storeToRefs(useFeatureFlagStore());

                                      const [calendarSyncGranted, delegatedGranted] =
                                          await Promise.all([
                                              handleFeatureFlagGuard(outlookCalendarSync),
                                              handleFeatureFlagGuard(delegatedCalendarAccess),
                                          ]);

                                      if (
                                          calendarSyncGranted === true ||
                                          delegatedGranted === true
                                      ) {
                                          return true;
                                      }

                                      return from ?? false;
                                  },
                              },
                              {
                                  path: 'mqtt-settings',
                                  name: 'AdminMqttSettings',
                                  component: () =>
                                      import('../admin/components/mqtt/MqttSettings.vue'),
                                  beforeEnter: () =>
                                      useFeatureFlagStore().mqttIntegration.isReady(),
                                  meta: {role: UserView.ROLE_ADMIN},
                              },
                              {
                                  path: 'domain/:accountId',
                                  name: 'AdminDomainList',
                                  component: () => import('../admin/components/DomainList.vue'),
                                  meta: {role: UserView.ROLE_ADMIN},
                              },
                              {
                                  path: 'api-keys/:accountId',
                                  name: 'AdminAPIKeyTable',
                                  component: () =>
                                      import('../admin/components/tables/APIKeyTable.vue'),
                                  meta: {adminAuth: true},
                                  beforeEnter: async (_, from) => {
                                      const {restApi} = storeToRefs(useFeatureFlagStore());
                                      return await handleFeatureFlagGuard(restApi, from);
                                  },
                              },
                              {
                                  path: 'floor/add/:accountId',
                                  name: 'AdminFloorAdd',
                                  component: () => import('../admin/components/forms/FloorAdd.vue'),
                                  meta: {role: UserView.ROLE_MANAGER},
                              },
                              {
                                  path: 'location/:locationId/floor/:floorId',
                                  name: 'AdminFloorEdit',
                                  component: () =>
                                      import('../admin/components/forms/FloorEdit.vue'),
                                  meta: {role: UserView.ROLE_MANAGER},
                              },
                              {
                                  path: 'location/add/:accountId',
                                  name: 'AdminLocationAdd',
                                  component: () =>
                                      import('../admin/components/forms/LocationAdd.vue'),
                                  meta: {role: UserView.ROLE_ADMIN},
                              },
                              {
                                  path: 'account/:accountId/location/:locationId',
                                  name: 'AdminLocationEdit',
                                  component: () =>
                                      import('../admin/components/forms/LocationEdit.vue'),
                                  meta: {role: UserView.ROLE_MANAGER},
                              },
                              {
                                  path: 'rooms/add/:accountId',
                                  name: 'AdminRoomAdd',
                                  component: () => import('../admin/components/forms/RoomAdd.vue'),
                                  meta: {role: UserView.ROLE_MANAGER},
                              },
                              {
                                  path: 'locations/:locationId/floor/:floorId/room/:roomId',
                                  name: 'AdminRoomEdit',
                                  component: () => import('../admin/components/forms/RoomEdit.vue'),
                                  meta: {role: UserView.ROLE_MANAGER},
                              },
                              {
                                  path: 'locations/:locationId/floor/:floorId/room/:roomId/bulk-add',
                                  name: 'AdminRoomBulkAdd',
                                  component: () =>
                                      import('../admin/components/forms/RoomBulkAdd.vue'),
                                  meta: {role: UserView.ROLE_ADMIN},
                              },
                              {
                                  path: '',
                                  name: 'AdminUsers',
                                  //   beforeEnter: userAdministrationGuard,
                                  component: () =>
                                      import('../shared/components/functional/KeepAliveRouterView.vue'),
                                  props: {
                                      include: 'UsersList',
                                  },
                                  children: [
                                      {
                                          path: 'users/:accountId',
                                          name: 'AdminUserList',
                                          component: () =>
                                              import('../admin/components/users/UsersList.vue'),
                                          meta: {role: UserView.ROLE_MANAGER},
                                          alias: 'users',
                                          props: true,
                                      },
                                      {
                                          path: 'users/add/:accountId',
                                          name: 'AdminUserAdd',
                                          component: () =>
                                              import('../admin/components/forms/UserAdd.vue'),
                                          meta: {role: UserView.ROLE_MANAGER},
                                          alias: 'users',
                                      },
                                      {
                                          path: 'account/:accountId/users/:userId',
                                          name: 'AdminUserEdit',
                                          component: () =>
                                              import('../admin/components/forms/UserEdit.vue'),
                                          meta: {role: UserView.ROLE_MANAGER},
                                          alias: 'users',
                                      },
                                  ],
                              },
                              {
                                  path: 'user-tag-management/:accountId',
                                  name: 'AdminUserTagManagement',
                                  component: () =>
                                      import('../admin/components/userTagManagement/UserTagManagement.vue'),
                                  meta: {role: UserView.ROLE_MANAGER},
                                  beforeEnter: async (_, from) => {
                                      const {userTags} = storeToRefs(useFeatureFlagStore());

                                      return await handleFeatureFlagGuard(userTags, from);
                                  },
                              },
                              {
                                  path: 'seat/:accountId',
                                  name: 'AdminSeatList',
                                  component: () => import('../admin/components/SeatList.vue'),
                                  meta: {role: UserView.ROLE_MANAGER},
                              },
                              {
                                  path: 'ai-assistant',
                                  name: 'AIAssistant',
                                  component: () =>
                                      import('../shared/components/functional/KeepAliveRouterView.vue'),
                                  beforeEnter: () => useFeatureFlagStore().aiAssistant.isReady(),
                                  meta: {role: UserView.ROLE_ANALYST},
                                  children: [
                                      {
                                          path: '',
                                          name: 'AdminAIAssistant',
                                          component: () =>
                                              import('../admin/components/AIAssistant.vue'),
                                      },
                                  ],
                              },
                              {
                                  path: 'kiosk-mode',
                                  name: 'KioskModeConfiguration',
                                  component: () =>
                                      import('../shared/components/functional/KeepAliveRouterView.vue'),
                                  beforeEnter: async (_, from) => {
                                      const {newKioskMode} = storeToRefs(useFeatureFlagStore());

                                      return await handleFeatureFlagGuard(newKioskMode, from);
                                  },
                                  children: [
                                      {
                                          path: '',
                                          name: 'KioskModeConfigurationList',
                                          component: () =>
                                              import('../admin/components/kioskMode/KioskModeList.vue'),
                                          meta: {role: UserView.ROLE_ADMIN},
                                      },
                                      {
                                          path: 'add',
                                          name: 'KioskModeConfigurationAdd',
                                          component: () =>
                                              import('../admin/components/kioskMode/form/KioskModeForm.vue'),
                                          meta: {role: UserView.ROLE_ADMIN},
                                      },
                                      {
                                          path: ':id',
                                          name: 'KioskModeConfigurationEdit',
                                          component: () =>
                                              import('../admin/components/kioskMode/form/KioskModeForm.vue'),
                                          meta: {role: UserView.ROLE_ADMIN},
                                          props: true,
                                      },
                                  ],
                              },
                              {
                                  path: 'booking-rules',
                                  name: 'BookingRules',
                                  meta: {role: UserView.ROLE_MANAGER},
                                  beforeEnter: async (_, from) => {
                                      const {bookingRules} = storeToRefs(useFeatureFlagStore());

                                      return await handleFeatureFlagGuard(bookingRules, from);
                                  },
                                  component: () =>
                                      import('../shared/components/functional/KeepAliveRouterView.vue'),
                                  children: [
                                      {
                                          path: '',
                                          name: 'BookingRulesList',
                                          component: () =>
                                              import('../admin/components/bookingRules/BookingRulesList.vue'),
                                      },
                                      {
                                          path: 'restriction',
                                          name: 'BookingRestriction',
                                          redirect: () => ({
                                              name: 'BookingRestrictionAdd',
                                          }),
                                          beforeEnter: async (_, from) => {
                                              const {bookingRuleRestriction} =
                                                  storeToRefs(useFeatureFlagStore());

                                              return await handleFeatureFlagGuard(
                                                  bookingRuleRestriction,
                                                  from
                                              );
                                          },
                                          children: [
                                              {
                                                  path: 'add',
                                                  name: 'BookingRestrictionAdd',
                                                  component: () =>
                                                      import('../admin/components/bookingRules/forms/RestrictionRuleForm.vue'),
                                                  props: true,
                                              },
                                              {
                                                  path: ':id',
                                                  name: 'BookingRestrictionEdit',
                                                  component: () =>
                                                      import('../admin/components/bookingRules/forms/RestrictionRuleForm.vue'),
                                                  props: true,
                                              },
                                          ],
                                      },
                                      {
                                          path: 'priority',
                                          name: 'BookingPriority',
                                          redirect: () => ({
                                              name: 'BookingPriorityAdd',
                                          }),
                                          beforeEnter: async (_, from) => {
                                              const {bookingRulePriority} =
                                                  storeToRefs(useFeatureFlagStore());

                                              return await handleFeatureFlagGuard(
                                                  bookingRulePriority,
                                                  from
                                              );
                                          },
                                          children: [
                                              {
                                                  path: 'add',
                                                  name: 'BookingPriorityAdd',
                                                  component: () =>
                                                      import('../admin/components/bookingRules/forms/PriorityRuleForm.vue'),
                                                  props: true,
                                              },
                                              {
                                                  path: ':id',
                                                  name: 'BookingPriorityEdit',
                                                  component: () =>
                                                      import('../admin/components/bookingRules/forms/PriorityRuleForm.vue'),
                                                  props: true,
                                              },
                                          ],
                                      },
                                      {
                                          path: 'permanent',
                                          name: 'BookingPermanent',
                                          redirect: () => ({
                                              name: 'BookingPermanentAdd',
                                          }),
                                          beforeEnter: async (_, from) => {
                                              const {bookingRulePermanent} =
                                                  storeToRefs(useFeatureFlagStore());

                                              return await handleFeatureFlagGuard(
                                                  bookingRulePermanent,
                                                  from
                                              );
                                          },
                                          children: [
                                              {
                                                  path: 'add',
                                                  name: 'BookingPermanentAdd',
                                                  component: () =>
                                                      import('../admin/components/bookingRules/forms/PermanentBookingRuleForm.vue'),
                                                  props: true,
                                              },
                                              {
                                                  path: ':id',
                                                  name: 'BookingPermanentEdit',
                                                  component: () =>
                                                      import('../admin/components/bookingRules/forms/PermanentBookingRuleForm.vue'),
                                                  props: true,
                                              },
                                          ],
                                      },
                                  ],
                              },
                              {
                                  path: 'characteristics/:accountId',
                                  name: 'AdminCharacteristicList',
                                  component: () =>
                                      import('../admin/components/CharacteristicList.vue'),
                                  meta: {role: UserView.ROLE_MANAGER},
                              },
                              {
                                  path: 'authorizationGroups/:accountId',
                                  name: 'AdminAuthorizationGroupTable',
                                  component: () =>
                                      import('../admin/components/tables/AuthorizationGroupTable.vue'),
                                  meta: {role: UserView.ROLE_MANAGER},
                              },
                              {
                                  path: 'license/:account',
                                  name: 'AdminLicenseWrapper',
                                  component: () =>
                                      import('../admin/components/license/LicenseWrapper.vue'),
                                  meta: {role: UserView.ROLE_ADMIN},
                                  children: [
                                      {
                                          path: 'overview',
                                          name: 'AdminLicenseOverview',
                                          component: () =>
                                              import('../admin/components/license/LicenseOverview.vue'),
                                      },
                                      {
                                          path: 'pricing',
                                          name: 'AdminPricingTable',
                                          component: () =>
                                              import('../admin/components/license/PricingTable.vue'),
                                      },
                                  ],
                              },
                              {
                                  path: 'hr/:accountId',
                                  name: 'AdminHrOverview',
                                  component: () =>
                                      import('../admin/components/kombo/HrOverview.vue'),
                                  meta: {role: UserView.ROLE_ADMIN},
                              },
                              {
                                  path: 'auditLog',
                                  name: 'AdminAuditLog',
                                  component: () =>
                                      import('../admin/components/auditLogs/AuditLogs.vue'),
                                  meta: {role: UserView.ROLE_ADMIN},
                              },
                              {
                                  path: 'deskly-connect',
                                  name: 'AdminDesklyConnect',
                                  beforeEnter: () => useFeatureFlagStore().desklyConnect.isReady(),
                                  component: () =>
                                      import('../admin/components/desklyConnect/DesklyConnect.vue'),
                                  meta: {role: UserView.ROLE_ADMIN},
                              },
                              {
                                  path: 'notifications',
                                  name: 'AdminNotifications',
                                  meta: {role: UserView.ROLE_SUPERADMIN},
                                  redirect: () => ({
                                      name: 'AdminNotificationsList',
                                  }),
                                  children: [
                                      {
                                          path: 'list',
                                          name: 'AdminNotificationsList',
                                          component: () =>
                                              import('../admin/components/notifications/NotificationsList.vue'),
                                      },
                                      {
                                          path: 'add',
                                          name: 'AdminNotificationsAdd',
                                          component: () =>
                                              import('../admin/components/notifications/NotificationsForm.vue'),
                                      },
                                      {
                                          path: 'edit/:id',
                                          name: 'AdminNotificationsEdit',
                                          component: () =>
                                              import('../admin/components/notifications/NotificationsForm.vue'),
                                          props: true,
                                      },
                                  ],
                              },
                          ],
                      },
                  ]),
            {
                path: '/logout',
                name: 'Logout',
                component: () => import('../frontstage/components/LogoutPage.vue'),
                meta: {
                    hideNavbar: true,
                },
            },
            {
                path: '/logout-microsoft',
                name: 'LogoutMicrosoft',
                beforeEnter: logoutMicrosoft,
            },
        ],
    },
    {
        // OAuth 2.1 consent screen. Renders its own minimal header (brand + language only) — no app
        // navigation or profile menu, so the user stays focused on the authorization decision.
        // Requires auth: anonymous users are sent to login by requireAuth and returned here after.
        path: '/oauth/authorize',
        name: 'OAuthConsent',
        beforeEnter: [requireAuth, requireOauthFeature],
        component: () => import('../frontstage/components/oauth/OAuthConsentView.vue'),
    },
    {
        name: 'Public',
        path: '',
        // TODO Find a better way for this
        beforeEnter: to => {
            const {isAuthenticated} = useUserStore();
            if (isAuthenticated) {
                return {name: 'Home'};
            }
            if (to.name === 'Public') {
                return {name: 'Login'};
            }
        },
        component: () => import('../layout/PublicLayout.vue'),
        children: [
            {
                path: '/login',
                name: 'Login',
                component: __IS_HYBRID_APP__
                    ? () => import('../frontstage/components/security/HybridAppLogin.vue')
                    : () => import('../frontstage/components/security/Login.vue'),
                beforeEnter: () => {
                    const {isAuthenticated} = useUserStore();
                    if (isAuthenticated) {
                        return {name: 'Home'};
                    }
                },
                meta: {public: true},
            },
            {
                path: '/callback',
                name: 'CallBack',
                component: () => import('../frontstage/components/security/Login.vue'),
                meta: {public: true},
                beforeEnter: authenticationCallbackRoute,
            },
            {
                path: '/azure',
                name: 'Azure',
                component: () => import('../frontstage/components/security/Login.vue'),
                meta: {public: true},
            },
            {
                path: '/paymentCanceled',
                name: 'PaymentCanceled',
                meta: {public: true},
                beforeEnter: PaymentCanceled,
            },
            {
                path: '/forgot-password',
                name: 'ForgotPassword',
                component: () => import('../frontstage/components/security/ForgotPassword.vue'),
                meta: {public: true},
            },
            {
                path: '/forgot-password-confirm',
                name: 'ForgotPasswordConfirm',
                component: () =>
                    import('../frontstage/components/security/ForgotPasswordConfirm.vue'),
                meta: {public: true},
            },
            {
                path: '/register',
                name: 'RegisterOverview',
                component: () => import('../frontstage/components/security/RegisterOverview.vue'),
                meta: {public: true},
            },
            {
                path: '/register-user/:id?',
                name: 'RegisterUser',
                component: () => import('../frontstage/components/security/RegisterUser.vue'),
                meta: {public: true},
            },
            {
                path: '/register-account',
                name: 'RegisterAccount',
                component: () => import('../frontstage/components/security/RegisterAccount.vue'),
                meta: {public: true},
            },
            {
                path: '/register-account-survey',
                name: 'RegisterAccountSurvey',
                component: () =>
                    import('../frontstage/components/security/RegisterAccountSurvey.vue'),
                meta: {public: true},
            },
            {
                path: '/register-account-confirm',
                name: 'RegisterAccountConfirm',
                component: () =>
                    import('../frontstage/components/security/RegisterAccountConfirm.vue'),
                meta: {public: true},
            },
            {
                path: '/resend-sso-verification',
                name: 'ResendSSOVerification',
                beforeEnter: resendSSOVerification,
                meta: {public: true},
            },
            {
                path: '/verify-sso/:token',
                name: 'VerifySSO',
                component: () =>
                    import('../frontstage/components/security/VerifySSOLandingPage.vue'),
                meta: {public: true},
            },
            {
                path: '/reset-password/:userId',
                name: 'ResetPassword',
                component: () => import('../frontstage/components/security/ResetPassword.vue'),
                props: true,
                meta: {public: true},
            },
            {
                path: '/confirm-user',
                name: 'ConfirmUser',
                meta: {public: true},
                beforeEnter: confirmUser,
            },
            {
                path: '/account-select',
                name: 'AccountSelect',
                component: () => import('../frontstage/components/security/AccountSelect.vue'),
                props: route => ({accounts: JSON.parse(route.query.accountsString)}),
                meta: {public: true},
            },
            {
                name: 'GoogleCallback',
                path: '/auth/google/callback',
            },
        ],
    },
    {
        path: '/kiosk-mode',
        name: 'KioskModeCode',
        component: () => import('../frontstage/components/kioskMode/KioskModeCode.vue'),
        meta: {hideNavbar: true, skipTokenRefresh: true},
    },
    {
        path: '/kiosk-mode/map/:roomId',
        name: 'KioskModeMap',
        component: () => import('../frontstage/components/kioskMode/KioskModeMap.vue'),
        props: true,
        meta: {hideNavbar: true, skipTokenRefresh: true},
    },
    {
        path: '/kiosk-mode/people/:roomId',
        name: 'KioskModePeople',
        component: () =>
            import('../frontstage/components/kioskMode/peopleView/KioskModePeople.vue'),
        props: true,
        meta: {hideNavbar: true, skipTokenRefresh: true},
    },
    // New kiosk mode
    {
        path: '/kiosk',
        name: 'KioskModeCodeNew',
        component: () => import('../frontstage/components/kioskMode/new/KioskModeCode.vue'),
        meta: {hideNavbar: true, skipTokenRefresh: true},
    },
    {
        path: '/kiosk/:token',
        name: 'KioskModeView',
        props: true,
        component: () => import('../frontstage/components/kioskMode/new/KioskModeView.vue'),
        meta: {hideNavbar: true, skipTokenRefresh: true},
    },
    {
        path: '/check-in/:token',
        name: 'CheckInConfirmation',
        beforeEnter: [handleCheckIn],
        props: route => ({result: route.meta.checkInResult ?? 'no_bookings'}),
        component: () => import('../frontstage/components/checkIn/CheckInConfirmation.vue'),
    },
    {
        path: '/authorize-sensor',
        name: 'AuthorizeSensor',
        beforeEnter: [handleAuthorizeSensor],
        component: () =>
            import('../frontstage/components/desklyConnect/SensorAuthorizationConfirmation.vue'),
    },
    {
        path: '/authorize-sensor-failed',
        name: 'AuthorizeSensorFailed',
        component: () =>
            import('../frontstage/components/desklyConnect/SensorAuthorizationFailed.vue'),
    },
    {
        path: '/:catchAll(.*)',
        name: 'NotFound',
        component: () => import('../shared/components/NotFound.vue'),
    },
];

export default createRouter({
    history: __IS_HYBRID_APP__
        ? createWebHashHistory()
        : createWebHistory(`/${getCurrentLocale()}/`),
    routes: wrapRouteGuards(routes),
    linkActiveClass: 'link-active',
});
